import { useCallback, useMemo, useState } from 'react';
import './EasyEstimatorGrid.scss'
import {
	Column,
	Item as GridToolbarItem,
	Lookup
} from 'devextreme-react/data-grid';
import dayjs from 'dayjs';
import { useGetEstimates } from "../api/get-estimates";
import { Link, useLocation } from 'react-router-dom';
import { LoadPagePanel, SyncDataGrid } from '../../../../components';
import { QuoteStatus, QuoteTypes } from '../../../../types/enums';
import CheckBox from 'devextreme-react/check-box';
import SelectBox from 'devextreme-react/select-box';
import { Item, Toolbar } from 'devextreme-react/toolbar';
import CreateEstimateButton from './CreateEstimateButton';

export default function EasyEstimatorGrid() {

	const datePeriods = [
		{ name: "Last 90 Days", dateFrom: dayjs().subtract(90, 'day').toDate().toDateString() },
		{ name: "Last 6 Months", dateFrom: dayjs().subtract(6, 'month').toDate().toDateString() },
		{ name: "Last Year", dateFrom: dayjs().subtract(1, 'year').toDate().toDateString() },
		{ name: "Last 2 Years", dateFrom: dayjs().subtract(2, 'year').toDate().toDateString() },
		{ name: "Last 3 Years", dateFrom: dayjs().subtract(3, 'year').toDate().toDateString() },
		{ name: "Entire History", dateFrom: null }
	];

	const [dateFrom, setDateFrom] = useState<string | null>(datePeriods[0].dateFrom);
	const [closedOrders, setClosedOrders] = useState(false);
	const [soldEstimates, setSoldEstimates] = useState(false);

	const estimates = useGetEstimates(closedOrders, dateFrom ? new Date(dateFrom) : null, false, true, soldEstimates);

	const location = useLocation();
	const returnUrl = encodeURIComponent(location.pathname + location.search);

	const rowPrepared = useCallback((e: any) => {
		if (e.rowType === "data" &&
			(e.data.quoteTypeID === QuoteTypes.EasyEstimator2 || e.data.quoteTypeID === QuoteTypes.EEPro) &&
			!e.data.submittedToIMEOn && e.data.statusID === QuoteStatus.Open) {
			e.rowElement.style.backgroundColor = "yellow";
		}
	}, []);

	const quoteNumberCellRender = useCallback((cell: any) => {
		return <Link to={`/cfi/estimate/${cell.data.quoteID}?returnUrl=${returnUrl}`}>
			{cell.data.quoteNumber}
		</Link>;
	}, [returnUrl]);

	const eeToolCellRender = useCallback((cell: any) => {
		if (cell.data.imeStatus !== 'SALE' && cell.data.quoteTypeID !== QuoteTypes.WinApp)
			return <>
				<Link to={`/cfi/easy-estimator/${cell.data.quoteID}`} title="EE Pro">
					<i className='dx-icon dx-icon-Inscyth-EEPro' />
				</Link>
				<a href={cell.data.easyEstimatorUrl + '?loadEstimate=' + cell.data.guid} target="_blank" rel="noreferrer" title="Estimator">
					<i className='dx-icon dx-icon-Inscyth-Easy-Estimator' />
				</a>
			</>;
		else if (cell.data.quoteTypeID === QuoteTypes.WinApp)
			return <>
				<Link onClick={() => { alert("Estimate was edited by Sage Support team and can no longer be accessed through the estimator tools"); }}
					rel="noreferrer" title="Estimator" role="button" to="">
					<i className='dx-icon dx-icon-Inscyth-Info' />
				</Link>
			</>;
		else
			return <></>;
	}, []);

	const roNumberCellRender = useCallback((cell: any) => {
		return (cell.data.retailOrderID ?
			<Link to={`/cfi/retail-order/${cell.data.retailOrderID}`}>
				{cell.data.roNumber}
			</Link>
			:
			<>{cell.data.roNumber}</>
		);
	}, []);

	return (
		<>
			<Toolbar className='easy-estimator-filter'>
				<Item location='before'>
					<SelectBox
						className='estimate-date-period-selectbox'
						value={dateFrom}
						valueExpr="dateFrom"
						displayExpr="name"
						dataSource={datePeriods}
						onValueChanged={(e) => setDateFrom(e.value)}
					/>
				</Item>
				<Item location='before'>
					<CheckBox
						className='closed-estimates-checkbox'
						value={closedOrders}
						text={"Closed/Expired Estimates"}
						onValueChanged={(e) => {
							setClosedOrders(e.value);
							if (!e.value)
								setSoldEstimates(false);
						}} />
				</Item>
				<Item location='before'>
					<CheckBox
						visible={closedOrders}
						className='sold-estimates-checkbox'
						value={soldEstimates}
						text={"Sold Estimates"}
						onValueChanged={(e) => setSoldEstimates(e.value)} />
				</Item>
			</Toolbar>

			<SyncDataGrid
				stateStorageKey='easy-estimator-grid'
				className={'dx-card wide-card'}
				dataSource={estimates.data?.estimates}
				keyExpr={'quoteID'}
				onRowPrepared={rowPrepared}
				height="62vh"
				additionalToolbarItems={[
					<GridToolbarItem
						location={'before'}
						key="create-estimate"
					>
						<CreateEstimateButton />
					</GridToolbarItem>
				]}
			>
				<Column
					dataField={'quoteNumber'}
					caption={'Estimate #'}
					dataType="string"
					cellRender={quoteNumberCellRender}
				/>
				<Column
                    dataField={'projectNumber'}
                    caption={'Project #'}
                    dataType="string"
                />
				<Column
					dataField={'quoteType'}
					caption={'Type'}
					dataType="string"
				>
					<Lookup
						dataSource={estimates.data?.quoteTypes}
					/>
				</Column>
				<Column
					caption={'EE Tool'}
					dataType="string"
					cssClass='ee-tool'
					alignment='center'
					cellRender={eeToolCellRender}
				/>
				<Column
					dataField={'projectNumber'}
					caption={'Project #'}
					dataType="string"
				/>
				<Column
					dataField={'customerName'}
					caption={'Customer'}
					dataType="string"
				/>
				<Column
					dataField={'customerAddress'}
					caption={'Address'}
					dataType="string"
				/>
				<Column
					dataField={'customerPhone'}
					caption={'Phone #'}
					dataType="string"
				/>
				<Column
					dataField={'customerEmail'}
					caption={'E-mail'}
					dataType="string"
				/>
				<Column
					dataField={'status'}
					caption={'Status'}
					dataType="string"
				>
					<Lookup
						dataSource={estimates.data?.statuses}
					/>
				</Column>
				<Column
					dataField={'dealerNumber'}
					caption={'Store #'}
					dataType="string"
				/>
				<Column
					dataField={'dealerName'}
					caption={'Store Name'}
					dataType="string"
				/>
				<Column
					dataField={'proxyDealerNumber'}
					caption={'Proxy Store #'}
					dataType="string"
				/>
				<Column
					dataField={'proxyDealerName'}
					caption={'Proxy Store Name'}
					dataType="string"
				/>
				<Column
					dataField={'actionRequired'}
					caption={'Action Required'}
					dataType="string"
				>
					<Lookup
						dataSource={estimates.data?.actionsRequired}
					/>
				</Column>
				<Column
					dataField={'imeStatus'}
					caption={'IME Status'}
					dataType="string"
				>
					<Lookup
						dataSource={estimates.data?.imeStatuses}
					/>
				</Column>
				<Column
					dataField={'submittedToIMEOn'}
					caption={'Submitted to IME'}
					dataType="date"
				/>
				<Column
					dataField={'cSale'}
					caption={'C-Sale'}
					dataType="boolean"
				/>
				<Column
					dataField={'customerContactDate'}
					caption={'Customer Contact Date'}
					dataType="date"
				/>
				<Column
					dataField={'scheduledAppointmentDate'}
					caption={'Scheduled Appointment Date'}
					dataType="date"
				/>
				<Column
					dataField={'roNumber'}
					caption={'RO #'}
					dataType="string"
					cellRender={roNumberCellRender}
				/>
				<Column
					dataField={'createdOn'}
					caption={'Creation Date'}
					dataType="date"
				/>
			</SyncDataGrid>

			<LoadPagePanel visible={estimates.isLoading} />
		</>
	)
} 
